import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { apiFetch } from '../lib/api-client';

interface Resume {
  id: number;
  name: string;
  filename: string;
  created_at: string;
  parse_status: string;
}

interface UseResumeContextReturn {
  selectedResumeId: string | null;
  selectedResume: Resume | null;
  resumes: Resume[];
  loading: boolean;
  error: string | null;
  setSelectedResumeId: (id: string) => void;
  refreshResumes: () => Promise<void>;
}

export function useResumeContext(): UseResumeContextReturn {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Load resumes on mount
  useEffect(() => {
    loadResumes();
  }, []);

  // Update selected resume when params or resumes change
  useEffect(() => {
    if (resumes.length > 0) {
      const urlResumeId = searchParams.get('resumeId');
      const storedResumeId = localStorage.getItem('selectedResumeId');
      
      let targetResumeId = urlResumeId || storedResumeId;
      
      if (targetResumeId) {
        const resume = resumes.find(r => r.id.toString() === targetResumeId);
        if (resume) {
          setSelectedResume(resume);
          return;
        }
      }
      
      // Default to most recent resume
      const mostRecent = resumes.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      
      if (mostRecent) {
        setSelectedResume(mostRecent);
        updateResumeId(mostRecent.id.toString());
      }
    }
  }, [resumes, searchParams]);

  const loadResumes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch('/resumes');
      const data = await response.json();
      
      if (data.success) {
        setResumes(data.data || []);
      } else {
        setError('Failed to load resumes');
      }
    } catch (err) {
      console.error('Failed to load resumes:', err);
      setError('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const updateResumeId = (resumeId: string) => {
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('resumeId', resumeId);
    setSearchParams(newParams);
    
    // Update localStorage
    localStorage.setItem('selectedResumeId', resumeId);
  };

  const setSelectedResumeId = (resumeId: string) => {
    const resume = resumes.find(r => r.id.toString() === resumeId);
    if (resume) {
      setSelectedResume(resume);
      updateResumeId(resumeId);
    }
  };

  return {
    selectedResumeId: selectedResume?.id.toString() || null,
    selectedResume,
    resumes,
    loading,
    error,
    setSelectedResumeId,
    refreshResumes: loadResumes
  };
}

// Utility function to get resumeId for API calls
export function getResumeIdForApi(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get('resumeId') || localStorage.getItem('selectedResumeId') || undefined;
}

// Utility function to add resumeId to API fetch calls
export function apiFetchWithResume(endpoint: string, options: RequestInit = {}) {
  const resumeId = getResumeIdForApi();
  
  if (resumeId && endpoint.includes('?')) {
    endpoint += `&resumeId=${resumeId}`;
  } else if (resumeId) {
    endpoint += `?resumeId=${resumeId}`;
  }
  
  return apiFetch(endpoint, options);
}